import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, ArrowDown, Scale, ShieldAlert, Sparkles, Compass,
  Briefcase, Coins, ShoppingBag, MapPin, Heart, Rocket, Plane,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";

const HERO_IMG = "https://images.unsplash.com/photo-1533162507191-d90c625b2640?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwY2FsbSUyMHplbiUyMG5hdHVyZXxlbnwwfHx8fDE3ODY1MjUzOTR8MA&ixlib=rb-4.1.0&q=85";

const rise = { initial: { opacity: 0, y: 18 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.5 } };

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLang();
  const go = () => navigate("/app");

  const STEPS = [
    { n: "1", t: t("landing.step1Title"), d: t("landing.step1Desc") },
    { n: "2", t: t("landing.step2Title"), d: t("landing.step2Desc") },
    { n: "3", t: t("landing.step3Title"), d: t("landing.step3Desc") },
    { n: "4", t: t("landing.step4Title"), d: t("landing.step4Desc") },
  ];
  const FLOW = [
    { icon: Compass, label: t("landing.flow1") },
    { icon: Scale, label: t("landing.flow2") },
    { icon: Scale, label: t("landing.flow3") },
    { icon: ShieldAlert, label: t("landing.flow4") },
    { icon: Sparkles, label: t("landing.flow5") },
    { icon: ArrowRight, label: t("landing.flow6") },
  ];
  const CATS = [
    { icon: Briefcase, label: t("cat.career") }, { icon: Coins, label: t("cat.money") },
    { icon: ShoppingBag, label: t("cat.purchases") }, { icon: MapPin, label: t("cat.moving") },
    { icon: Heart, label: t("cat.relationships") }, { icon: Rocket, label: t("cat.business") },
    { icon: Plane, label: t("cat.travel") }, { icon: Compass, label: t("cat.life") },
  ];

  return (
    <div className="min-h-screen bg-decide-canvas">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-decide-canvas/80 border-b border-decide-line/60">
        <div className="mx-auto max-w-5xl flex items-center justify-between px-6 h-16">
          <Logo size="md" />
          <div className="flex items-center gap-2">
            <button data-testid="nav-pricing" onClick={() => navigate("/pricing")} className="hidden sm:inline-flex btn-ghost text-sm">{t("nav.pricing")}</button>
            <button data-testid="landing-cta-nav" onClick={go} className="btn-primary !py-2.5 !px-5 text-sm">{user ? t("nav.openApp") : t("nav.makeDecision")}</button>
          </div>
        </div>
      </header>

      <section className="relative overflow-hidden">
        <div className="mx-auto max-w-5xl px-6 pt-16 pb-20 sm:pt-24 sm:pb-28 grid md:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-2 rounded-full border border-decide-line bg-decide-surface px-3 py-1.5 text-[12px] font-medium text-decide-slate">
              <span className="h-1.5 w-1.5 rounded-full bg-decide-forest" /> {t("brand.tagline")}
            </div>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.03] tracking-tight text-decide-ink">
              {t("landing.heroTitle")}
            </h1>
            <p className="mt-5 text-lg leading-relaxed text-decide-slate max-w-md">{t("landing.heroSub")}</p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button data-testid="landing-cta-hero" onClick={go} className="btn-accent">{t("nav.makeDecision")} <ArrowRight size={18} /></button>
              <button data-testid="landing-example" onClick={go} className="btn-secondary">{t("landing.seeExample")}</button>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.1 }} className="relative">
            <div className="relative rounded-3xl border border-decide-line bg-decide-surface p-5 shadow-[0_20px_60px_rgba(0,0,0,0.06)]">
              <img src={HERO_IMG} alt="" className="h-40 w-full rounded-2xl object-cover" />
              <div className="mt-4">
                <p className="decide-label">{t("landing.previewRec")}</p>
                <p className="mt-1.5 text-xl font-medium tracking-tight text-decide-ink">{t("landing.previewRecValue")}</p>
                <span className="mt-2 inline-flex rounded-full bg-decide-forest px-2.5 py-1 text-[11px] font-semibold text-white">{t("landing.previewConfidence")}</span>
              </div>
              <div className="mt-4 rounded-2xl bg-decide-mist p-4">
                <p className="text-[11px] font-bold uppercase tracking-widest text-decide-forest">{t("landing.previewTradeoff")}</p>
                <p className="mt-1.5 text-[14px] leading-relaxed text-decide-ink">{t("landing.previewTradeoffText")}</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="border-t border-decide-line bg-decide-surface">
        <div className="mx-auto max-w-5xl px-6 py-20">
          <motion.p {...rise} className="decide-label">{t("landing.howItWorks")}</motion.p>
          <motion.h2 {...rise} className="mt-3 text-2xl sm:text-3xl font-medium tracking-tight text-decide-ink max-w-lg">{t("landing.howTitle")}</motion.h2>
          <div className="mt-10 grid sm:grid-cols-2 gap-x-10 gap-y-8">
            {STEPS.map((s, i) => (
              <motion.div key={s.n} {...rise} transition={{ duration: 0.5, delay: i * 0.05 }} className="flex gap-4">
                <span className="font-mono text-sm text-decide-forest mt-1">{s.n}</span>
                <div>
                  <h3 className="text-lg font-semibold text-decide-ink">{s.t}</h3>
                  <p className="mt-1.5 text-decide-slate leading-relaxed">{s.d}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 py-20">
        <motion.h2 {...rise} className="text-2xl sm:text-3xl font-medium tracking-tight text-decide-ink">{t("landing.notJustTitle")}</motion.h2>
        <motion.p {...rise} className="mt-3 text-lg text-decide-slate max-w-md">{t("landing.notJustSub")}</motion.p>
        <div className="mt-10 mx-auto max-w-sm">
          {FLOW.map((f, i) => (
            <motion.div key={f.label} {...rise} transition={{ duration: 0.4, delay: i * 0.06 }} className="flex flex-col items-center">
              <div className="flex w-full items-center gap-4 rounded-2xl border border-decide-line bg-decide-surface px-5 py-4">
                <f.icon size={20} className="text-decide-forest" strokeWidth={1.7} />
                <span className="font-medium text-decide-ink">{f.label}</span>
              </div>
              {i < FLOW.length - 1 && <ArrowDown size={16} className="my-1.5 text-decide-mute" />}
            </motion.div>
          ))}
        </div>
      </section>

      <section className="border-t border-decide-line bg-decide-surface">
        <div className="mx-auto max-w-5xl px-6 py-20">
          <motion.p {...rise} className="decide-label">{t("landing.builtLabel")}</motion.p>
          <motion.h2 {...rise} className="mt-3 text-2xl sm:text-3xl font-medium tracking-tight text-decide-ink">{t("landing.builtTitle")}</motion.h2>
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {CATS.map((c, i) => (
              <motion.div key={c.label} {...rise} transition={{ duration: 0.4, delay: i * 0.04 }} className="flex flex-col items-start gap-3 rounded-2xl border border-decide-line bg-decide-canvas p-5">
                <c.icon size={22} className="text-decide-forest" strokeWidth={1.7} />
                <span className="font-medium text-decide-ink">{c.label}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 py-24 text-center">
        <motion.h2 {...rise} className="text-3xl sm:text-4xl font-light tracking-tight text-decide-ink max-w-xl mx-auto leading-tight">{t("landing.ctaTitle")}</motion.h2>
        <motion.p {...rise} className="mt-4 text-decide-slate">{t("landing.ctaSub")}</motion.p>
        <motion.div {...rise} className="mt-8">
          <button data-testid="landing-cta-footer" onClick={go} className="btn-accent">{t("nav.makeDecision")} <ArrowRight size={18} /></button>
        </motion.div>
      </section>

      <footer className="border-t border-decide-line">
        <div className="mx-auto max-w-5xl px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <Logo size="sm" />
          <p className="text-[13px] text-decide-mute">{t("landing.footer")}</p>
        </div>
      </footer>
    </div>
  );
}
